import { useMemo, useState } from 'react'
import type { AbilityGrades, Player, DevelopmentAllocation, StaffMember } from '@/game/types'

const DEV_FOCUSES = [
  'tactical discipline',
  'kicking accuracy',
  'contested ball',
  'defensive craft',
  'goal sense',
  'speed & acceleration',
  'endurance base',
  'strength & power',
  'decision making',
  'leadership',
  'ruck work',
  'ground ball',
  'marking',
  'handball',
  'recovery',
  'durability',
] as const

const DEVELOPMENT_PROGRAMS = [
  { id: 'young-player-group', name: 'Emerging-player programme', focus: 'general development', description: 'Shared pathway work for players aged 21 and under. This is programme capacity, not a targeted player allocation.' },
  { id: 'tactical-cohesion', name: 'Team tactical cohesion', focus: 'team cohesion', description: 'Builds whole-team system familiarity. Each point adds 0.7 tactical cohesion when the round advances.' },
  { id: 'veteran-recovery', name: 'Veteran recovery programme', focus: 'recovery', description: 'Protects recovery work for the senior group and is reported separately in weekly consequences.' },
] as const

type AbilityKey = keyof AbilityGrades
type DevelopmentSortKey = 'roster' | 'age' | AbilityKey

const FOCUS_ABILITIES: Record<string, AbilityKey[]> = {
  general: ['kicking', 'decisionMaking', 'endurance'],
  'tactical discipline': ['tacticalDiscipline', 'positioning', 'decisionMaking'],
  'kicking accuracy': ['kicking', 'composure', 'decisionMaking'],
  'contested ball': ['contestedAbility', 'groundBall', 'strength'],
  'defensive craft': ['defensiveCraft', 'tackling', 'positioning'],
  'goal sense': ['goalSense', 'kicking', 'composure'],
  'speed & acceleration': ['speed', 'acceleration', 'agility'],
  'endurance base': ['endurance', 'recovery', 'durability'],
  'strength & power': ['strength', 'contestedAbility', 'leap'],
  'decision making': ['decisionMaking', 'anticipation', 'spatialAwareness'],
  leadership: ['leadership', 'professionalism', 'resilience'],
  'ruck work': ['ruckWork', 'leap', 'strength'],
  'ground ball': ['groundBall', 'agility', 'contestedAbility'],
  marking: ['marking', 'leap', 'positioning'],
  handball: ['handball', 'decisionMaking', 'spatialAwareness'],
  recovery: ['recovery', 'durability', 'resilience'],
  durability: ['durability', 'recovery', 'resilience'],
}

const abilityLabel = (key: AbilityKey) => key.replace(/([A-Z])/g, ' $1').replace(/^./, letter => letter.toUpperCase())

const gradeTone = (grade: string) => {
  if (['Exceptional', 'Elite', 'Strong', 'Above Standard'].includes(grade)) return 'positive'
  if (['Limited', 'Below Standard'].includes(grade)) return 'warning'
  return ''
}

interface DevelopmentPlanProps {
  players: Player[]
  allocations: DevelopmentAllocation[]
  staffBaseline: DevelopmentAllocation[]
  capacity: number
  coachingStaff: StaffMember[]
  onUpdate: (allocations: DevelopmentAllocation[]) => void
  onComplete: () => void
}

const gradeValue = (grade?: string) => {
  const grades = ['Limited', 'Below Standard', 'League Standard', 'Above Standard', 'Strong', 'Elite', 'Exceptional']
  return grade ? Math.max(0, grades.indexOf(grade)) : 0
}

const assistantFit = (assistant: StaffMember, player: Player, focus: string) => {
  const lowerFocus = focus.toLowerCase()
  const role = player.primaryRole.toLowerCase()
  let score = gradeValue(assistant.abilities.Teaching)
  const reasons: string[] = []

  if (assistant.id === 'grant-havel') {
    if (lowerFocus.includes('defensive') || lowerFocus.includes('marking')) { score += 12; reasons.push('defensive and positioning specialist') }
    else if (role.includes('defender')) { score += 6; reasons.push('backline role familiarity') }
  }
  if (assistant.id === 'mara-keane') {
    if (['contested', 'ground ball', 'handball', 'decision'].some(term => lowerFocus.includes(term))) { score += 12; reasons.push('midfield-development specialist') }
    else if (player.age <= 22) { score += 6; reasons.push('strong emerging-player teacher') }
  }
  if (assistant.id === 'ivo-sayer') {
    if (['goal', 'kicking', 'marking'].some(term => lowerFocus.includes(term))) { score += 12; reasons.push('forward craft and conversion specialist') }
    else if (role.includes('forward')) { score += 6; reasons.push('forward-line role familiarity') }
  }
  if (assistant.id === 'tessa-byrne' && ['speed', 'endurance', 'strength', 'recovery', 'durability'].some(term => lowerFocus.includes(term))) {
    score += 12; reasons.push('physical preparation and recovery specialist')
  }

  const workloadPenalty = assistant.workload >= 85 ? 3 : assistant.workload >= 75 ? 1 : 0
  score -= workloadPenalty
  if (workloadPenalty) reasons.push(`${assistant.workload}% workload limits attention`)
  if (!reasons.length) reasons.push('general teaching support; limited focus overlap')
  return { score, explanation: reasons.join(' · ') }
}

const rankAssistants = (staff: StaffMember[], player: Player, focus: string) => [...staff]
  .map((assistant, index) => ({ assistant, fit: assistantFit(assistant, player, focus || 'general'), index }))
  .sort((a, b) => Math.abs(b.fit.score - a.fit.score) > 1 ? b.fit.score - a.fit.score : a.index - b.index)

const allocationFingerprint = (allocations: DevelopmentAllocation[]) => JSON.stringify(
  [...allocations]
    .map(({ playerId, focus, points, isLocked, assistant }) => ({ playerId, focus, points, isLocked, assistant: assistant ?? null }))
    .sort((a, b) => a.playerId.localeCompare(b.playerId)),
)

export function DevelopmentPlan({ players, allocations, staffBaseline, capacity, coachingStaff, onUpdate, onComplete }: DevelopmentPlanProps) {
  const [statsPlayerId, setStatsPlayerId] = useState<string | null>(null)
  const [fullProfilePlayerId, setFullProfilePlayerId] = useState<string | null>(null)
  const [sortKey, setSortKey] = useState<DevelopmentSortKey>('roster')
  const [sortDirection, setSortDirection] = useState<'ascending' | 'descending'>('ascending')
  const used = useMemo(() => allocations.reduce((s, a) => s + a.points, 0), [allocations])
  const remaining = capacity - used
  const allocMap = useMemo(() => new Map(allocations.map(a => [a.playerId, a])), [allocations])
  const abilityKeys = useMemo(() => Object.keys(players[0]?.abilities ?? {}) as AbilityKey[], [players])
  const sortedPlayers = useMemo(() => {
    if (sortKey === 'roster') return players
    return [...players].sort((a, b) => {
      const comparison = sortKey === 'age'
        ? a.age - b.age
        : gradeValue(a.abilities[sortKey]) - gradeValue(b.abilities[sortKey])
      const directed = sortDirection === 'ascending' ? comparison : -comparison
      return directed || a.name.localeCompare(b.name)
    })
  }, [players, sortKey, sortDirection])
  const isStaffPlan = useMemo(
    () => allocationFingerprint(allocations) === allocationFingerprint(staffBaseline),
    [allocations, staffBaseline],
  )

  const updatePlan = (next: DevelopmentAllocation[]) => {
    onUpdate(next)
  }

  const getAlloc = (playerId: string) => allocMap.get(playerId)

  const inc = (playerId: string) => {
    if (remaining <= 0) return
    const current = getAlloc(playerId)
    const next = [...allocations]
    if (current) {
      const idx = next.findIndex(a => a.playerId === playerId)
      next[idx] = { ...current, points: current.points + 1, isLocked: true }
    } else {
      const program = DEVELOPMENT_PROGRAMS.find(entry => entry.id === playerId)
      const player = players.find(entry => entry.id === playerId)
      const assistant = player ? rankAssistants(coachingStaff, player, 'general')[0]?.assistant.id : undefined
      next.push({ playerId, focus: program?.focus ?? 'general', points: 1, isLocked: true, assistant })
    }
    updatePlan(next)
  }

  const dec = (playerId: string) => {
    const current = getAlloc(playerId)
    if (!current) return
    const next = [...allocations]
    const idx = next.findIndex(a => a.playerId === playerId)
    if (current.points <= 1) {
      next.splice(idx, 1)
    } else {
      next[idx] = { ...current, points: current.points - 1, isLocked: true }
    }
    updatePlan(next)
  }

  const setFocus = (playerId: string, focus: string) => {
    const current = getAlloc(playerId)
    if (!current) return
    const next = [...allocations]
    const idx = next.findIndex(a => a.playerId === playerId)
    next[idx] = { ...current, focus, isLocked: true }
    updatePlan(next)
  }

  const setAssistant = (playerId: string, assistant: string) => {
    const current = getAlloc(playerId)
    if (!current) return
    const next = [...allocations]
    const idx = next.findIndex(a => a.playerId === playerId)
    next[idx] = { ...current, assistant, isLocked: true }
    updatePlan(next)
  }

  const restoreStaff = () => {
    onUpdate(staffBaseline.map(allocation => ({ ...allocation })))
  }

  const renderFace = (player: Player) => {
    const v = player.visual
    if (!v) return <svg viewBox="0 0 100 100" width="56" height="56"><circle cx="50" cy="50" r="45" fill="var(--gold)" opacity="0.2"/><text x="50" y="55" textAnchor="middle" fill="var(--gold)" fontSize="20" fontWeight="700">{player.name.charAt(0)}</text></svg>
    return (
      <svg viewBox="0 0 100 100" width="56" height="56" role="img" aria-label={v.description}>
        <defs>
          <radialGradient id={`dev-face-${player.id}`} cx="50%" cy="30%" r="50%">
            <stop offset="0%" stopColor="var(--gold)" stopOpacity="0.15"/>
            <stop offset="70%" stopColor="var(--panel-2)" stopOpacity="1"/>
          </radialGradient>
        </defs>
        <circle cx="50" cy="35" r="28" fill={`url(#dev-face-${player.id})`} />
        <ellipse cx="40" cy="28" rx="5" ry="4" fill="var(--gold)" opacity="0.3"/>
        <ellipse cx="60" cy="28" rx="5" ry="4" fill="var(--gold)" opacity="0.3"/>
        <path d="M35 48 Q50 54 65 48" stroke="var(--gold)" strokeWidth="2" fill="none" opacity="0.4"/>
      </svg>
    )
  }

  const diminishing = (pts: number) => pts > 5 ? 'severe' : pts > 3 ? 'moderate' : ''

  const hasPoints = allocations.length > 0

  return (
    <div className="screen">
      <div className="page-title">
        <div>
          <h1>Development Plan</h1>
          <p>
            <span style={{fontWeight: 700, color: 'var(--ink)', fontSize: '18px'}}>{used}</span> / {capacity} allocated ·
            <span style={{fontWeight: 700, color: remaining < 2 ? 'var(--danger)' : 'var(--good)', fontSize: '18px'}}>{remaining}</span> remaining
          </p>
        </div>
        <button className="text-button" onClick={restoreStaff} disabled={isStaffPlan || !hasPoints} aria-live="polite">
          {isStaffPlan ? 'Staff Plan Active' : 'Restore Staff Plan'}
        </button>
      </div>

      <div className={`development-instruction ${remaining === 0 ? 'capacity-full' : ''}`}>
        <strong>{remaining === 0 ? 'All 10 points are allocated.' : remaining === 1 ? '1 point remains.' : `${remaining} points remain.`}</strong>
        <span>To move a point: press <b>−</b> on one player, then press <b>+</b> on another. Changes save immediately.</span>
      </div>

      <section className="development-sort" aria-label="Development player sorting">
        <div>
          <label htmlFor="development-sort-key">Sort players by</label>
          <select
            id="development-sort-key"
            aria-label="Sort development players by"
            value={sortKey}
            onChange={event => {
              const nextKey = event.target.value as DevelopmentSortKey
              setSortKey(nextKey)
              setSortDirection(nextKey === 'age' || nextKey === 'roster' ? 'ascending' : 'descending')
            }}
          >
            <option value="roster">Staff roster order</option>
            <option value="age">Age</option>
            {abilityKeys.map(key => <option key={key} value={key}>{abilityLabel(key)}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="development-sort-direction">Order</label>
          <select
            id="development-sort-direction"
            aria-label="Development sort order"
            value={sortDirection}
            onChange={event => setSortDirection(event.target.value as 'ascending' | 'descending')}
            disabled={sortKey === 'roster'}
          >
            <option value="ascending">{sortKey === 'age' ? 'Youngest to oldest' : 'Weakest to strongest'}</option>
            <option value="descending">{sortKey === 'age' ? 'Oldest to youngest' : 'Strongest to weakest'}</option>
          </select>
        </div>
        <p>{sortKey === 'roster' ? 'Preserves the football department’s roster sequence.' : sortKey === 'age' ? 'Age is a real input to development efficiency and list planning.' : `Sorted using visible staff estimates for ${abilityLabel(sortKey)}; hidden exact ratings remain undisclosed.`}</p>
      </section>

      <section className="panel development-programme-panel">
        <div className="section-heading">
          <div><span className="eyebrow">Shared allocations</span><h2>Group Programmes</h2></div>
          <strong>{DEVELOPMENT_PROGRAMS.reduce((total, program) => total + (getAlloc(program.id)?.points ?? 0), 0)} points</strong>
        </div>
        <p className="development-programme-explainer">These points were previously counted but not shown. Reduce a programme to release capacity, then assign that capacity to an individual player below.</p>
        <div className="development-programme-grid">
          {DEVELOPMENT_PROGRAMS.map(program => {
            const points = getAlloc(program.id)?.points ?? 0
            return <article key={program.id} className="development-programme-card" data-testid="development-programme-card" data-programme-id={program.id} data-programme-points={points}>
              <div><span className="eyebrow">Group programme</span><h3>{program.name}</h3><p>{program.description}</p></div>
              <div className={`development-stepper ${points > 0 ? 'active' : ''}`}>
                <button type="button" onClick={() => dec(program.id)} disabled={points === 0} aria-label={`Remove one development point from ${program.name}`}>−</button>
                <div><strong>{points}</strong><span>Development points</span></div>
                <button type="button" onClick={() => inc(program.id)} disabled={remaining <= 0} aria-label={`Add one development point to ${program.name}`}>+</button>
              </div>
            </article>
          })}
        </div>
      </section>

      <div className="panel" style={{padding: '0', overflow: 'hidden'}}>
        <div className="section-heading">
          <div>
            <span className="eyebrow">Current Allocation</span>
            <h2>Development Assignments</h2>
          </div>
        </div>
        <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '12px', padding: '16px'}}>
          {sortedPlayers.map(player => {
            const alloc = getAlloc(player.id)
            const pts = alloc?.points ?? 0
            const warn = diminishing(pts)
            const statsOpen = statsPlayerId === player.id
            const fullProfileOpen = fullProfilePlayerId === player.id
            const relevantAbilities = FOCUS_ABILITIES[alloc?.focus || 'general'] ?? FOCUS_ABILITIES.general
            const allAbilities = Object.entries(player.abilities) as [AbilityKey, Player['abilities'][AbilityKey]][]
            const assistantOptions = rankAssistants(coachingStaff, player, alloc?.focus ?? 'general')
            const selectedAssistant = assistantOptions.find(option => option.assistant.id === alloc?.assistant) ?? assistantOptions[0]

            return (
              <div
                key={player.id}
                data-testid="development-player-card"
                data-player-name={player.name}
                data-player-age={player.age}
                data-player-points={pts}
                style={{
                  background: pts > 0 ? 'linear-gradient(90deg, var(--gold-soft), var(--panel))' : 'var(--panel)',
                  border: '1px solid var(--line)',
                  borderRadius: '6px',
                  overflow: 'hidden',
                }}
              >
                <div className="development-player-card-body">
                    <div className="player-card-face">
                      {renderFace(player)}
                      <div className="player-card-name">{player.name}</div>
                      <div className="player-card-role">{player.primaryRole}</div>
                      <div className="player-card-position">{player.position} · Age {player.age}</div>
                      {sortKey !== 'roster' && sortKey !== 'age' && <div className="development-sorted-grade">{abilityLabel(sortKey)} · <strong>{player.abilities[sortKey]}</strong></div>}
                    </div>

                    <button
                      type="button"
                      className={`development-stats-toggle ${statsOpen ? 'active' : ''}`}
                      onClick={() => {
                        setStatsPlayerId(statsOpen ? null : player.id)
                        if (statsOpen) setFullProfilePlayerId(null)
                      }}
                      aria-expanded={statsOpen}
                      aria-label={`${statsOpen ? 'Hide' : 'View'} ${player.name} statistics`}
                    >
                      {statsOpen ? 'Hide stats' : 'View stats'}
                    </button>

                    {statsOpen && (
                      <div className="development-stats-panel">
                        <div className="development-condition-row">
                          <span>Fitness <strong>{player.fitness}</strong></span>
                          <span>Form <strong>{player.form}</strong></span>
                          <span>Morale <strong>{player.morale}</strong></span>
                          <span>Fatigue <strong>{player.fatigue}</strong></span>
                        </div>
                        <div className="development-progress-brief">
                          <span>Banked progress <strong>{player.development.progress}</strong></span>
                          <span>Review range <strong>{player.development.reviewWindow.min}–{player.development.reviewWindow.max}</strong></span>
                          <span>Role familiarity <strong>{player.development.roleFamiliarity}</strong></span>
                          <span>Senior matches <strong>{player.development.seniorMatches}</strong></span>
                        </div>
                        <p>Relevant to: <strong>{alloc?.focus || 'general development'}</strong></p>
                        <div className="development-ability-list">
                          {relevantAbilities.map(key => (
                            <div key={key}>
                              <span>{abilityLabel(key)}</span>
                              <strong className={gradeTone(player.abilities[key])}>{player.abilities[key]}</strong>
                            </div>
                          ))}
                        </div>
                        <button
                          type="button"
                          className="text-button development-full-profile-toggle"
                          onClick={() => setFullProfilePlayerId(fullProfileOpen ? null : player.id)}
                          aria-expanded={fullProfileOpen}
                          aria-label={`${fullProfileOpen ? 'Hide' : 'Open'} full profile for ${player.name}`}
                        >
                          {fullProfileOpen ? 'Hide full profile' : 'Open full profile'}
                        </button>

                        {fullProfileOpen && (
                          <section className="development-full-profile" aria-label={`${player.name} full profile`}>
                            <div className="development-profile-summary">
                              <span>Age <strong>{player.age}</strong></span>
                              <span>Position <strong>{player.position}</strong></span>
                              <span>Role <strong>{player.primaryRole}</strong></span>
                              <span>Contract <strong>{player.contractYears} years</strong></span>
                            </div>
                            {player.staffConcern && <div className="development-profile-concern"><strong>Staff concern</strong><span>{player.staffConcern}</span></div>}
                            <div className="development-history">
                              <strong>Development history</strong>
                              {player.development.history.length ? [...player.development.history].reverse().map(item => <div key={item.round}><span>Round {item.round} · +{item.progressGained} · {item.matchExposure ? 'senior exposure' : 'training only'}</span><p>{item.summary}</p></div>) : <p>No completed weekly reviews yet.</p>}
                            </div>
                            <div className="development-full-abilities">
                              {allAbilities.map(([key, grade]) => (
                                <div key={key} className={relevantAbilities.includes(key) ? 'focus-relevant' : ''}>
                                  <span>{abilityLabel(key)}</span>
                                  <strong className={gradeTone(grade)}>{grade}</strong>
                                </div>
                              ))}
                            </div>
                            <p className="confidence-note">Staff estimates. Word grades are not fixed ceilings; hidden exact values and latent traits remain undisclosed.</p>
                          </section>
                        )}
                      </div>
                    )}

                    <div className={`development-stepper ${pts > 0 ? 'active' : ''}`}>
                      <button
                        type="button"
                        onClick={() => dec(player.id)}
                        disabled={pts === 0}
                        aria-label={`Remove one development point from ${player.name}`}
                      >−</button>
                      <div>
                        <strong>{pts}</strong>
                        <span>Development points</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => inc(player.id)}
                        disabled={remaining <= 0}
                        aria-label={`Add one development point to ${player.name}`}
                      >+</button>
                    </div>

                    {pts > 0 && (
                      <div style={{
                        display: 'flex', flexDirection: 'column', gap: '8px',
                        marginTop: '8px', padding: '10px 12px',
                        background: 'var(--panel-2)', borderRadius: '6px',
                        border: '1px solid var(--line)'
                      }}>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap'}}>
                          <div style={{fontSize: '11px', color: 'var(--muted)', textTransform: 'uppercase'}}>Focus</div>
                          <select
                            aria-label={`Development focus for ${player.name}`}
                            value={alloc?.focus ?? ''}
                            onChange={e => setFocus(player.id, e.target.value)}
                            disabled={pts === 0}
                            style={{
                              flex: 1, minWidth: '140px', padding: '6px 10px', fontSize: '12px',
                              borderRadius: '4px', border: '1px solid var(--line)',
                              background: 'var(--panel)', color: 'var(--ink)'
                            }}
                          >
                            <option value="">General</option>
                            {DEV_FOCUSES.map(f => <option key={f} value={f}>{f}</option>)}
                          </select>
                        </div>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap'}}>
                          <div style={{fontSize: '11px', color: 'var(--muted)', textTransform: 'uppercase'}}>Assistant</div>
                          <select
                            aria-label={`Development assistant for ${player.name}`}
                            value={alloc?.assistant ?? assistantOptions[0]?.assistant.id ?? ''}
                            onChange={e => setAssistant(player.id, e.target.value)}
                            disabled={pts === 0}
                            style={{
                              flex: 1, minWidth: '140px', padding: '6px 10px', fontSize: '12px',
                              borderRadius: '4px', border: '1px solid var(--line)',
                              background: 'var(--panel)', color: 'var(--ink)'
                            }}
                          >
                            {assistantOptions.map(({ assistant }, index) => (
                              <option key={assistant.id} value={assistant.id}>
                                {assistant.name} — {assistant.role}{index === 0 ? ' · Recommended' : ''}
                              </option>
                            ))}
                          </select>
                        </div>
                        {selectedAssistant && (
                          <div className="assistant-fit" aria-live="polite">
                            <strong>{selectedAssistant.assistant.id === assistantOptions[0]?.assistant.id ? 'Recommended fit' : 'Selected fit'}</strong>
                            <span>{selectedAssistant.fit.explanation}</span>
                          </div>
                        )}

                        {warn && (
                          <div style={{fontSize: '10px', color: 'var(--danger)', textAlign: 'center', paddingTop: '2px'}}>
                            ⚠ Diminishing returns: {warn}
                          </div>
                        )}
                      </div>
                    )}

                    <div className="development-provenance">
                      {pts === 0 ? 'No allocation' : 'Coach allocation — changes save immediately'}
                    </div>
                </div>
              </div>
            )
          })}
          {!hasPoints && (
            <div style={{gridColumn: '1 / -1', textAlign: 'center', padding: '32px', color: 'var(--dim)'}}>
              No development allocations yet. Press <strong style={{color: 'var(--gold)'}}>+</strong> on any player to begin.
            </div>
          )}
        </div>
      </div>
      <div className="sticky-actions">
        <span>{remaining === 0 ? 'Development plan complete — changes saved' : `${remaining} point${remaining === 1 ? '' : 's'} must still be allocated`}</span>
        <button className="primary-action" type="button" disabled={remaining !== 0} onClick={onComplete}>Save Development Plan — Return to Week</button>
      </div>
    </div>
  )
}